import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BatchProcessingPanel = ({ jobs, onStartBatch, onCancelJob }) => {
  const [showJobHistory, setShowJobHistory] = useState(false);

  const getJobStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return { icon: 'CheckCircle', color: 'text-success' };
      case 'processing':
        return { icon: 'Loader', color: 'text-primary animate-spin' };
      case 'failed':
        return { icon: 'XCircle', color: 'text-destructive' };
      case 'queued':
        return { icon: 'Clock', color: 'text-warning' };
      default:
        return { icon: 'Circle', color: 'text-text-secondary' };
    }
  };

  const getJobStatusBadge = (status) => {
    const statusConfig = {
      completed: 'bg-success text-success-foreground',
      processing: 'bg-primary text-primary-foreground',
      failed: 'bg-destructive text-destructive-foreground',
      queued: 'bg-warning text-warning-foreground'
    };
    
    return statusConfig?.[status] || 'bg-muted text-text-secondary';
  };

  const formatJobTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const activeJobs = jobs?.filter(job => job?.status === 'processing' || job?.status === 'queued');
  const completedJobs = jobs?.filter(job => job?.status === 'completed' || job?.status === 'failed');

  return (
    <div className="bg-card border border-border rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Batch Processing</h2>
          <Button
            variant="default"
            size="sm"
            onClick={onStartBatch}
            iconName="Play"
            iconPosition="left"
          >
            Start Batch Revaluation
          </Button>
        </div>
      </div>
      <div className="p-6">
        {/* Active Jobs */}
        {activeJobs?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-text-primary mb-3">Active Jobs</h3>
            <div className="space-y-3">
              {activeJobs?.map((job) => {
                const statusIcon = getJobStatusIcon(job?.status);
                return (
                  <div key={job?.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name={statusIcon?.icon} size={20} className={statusIcon?.color} />
                      <div>
                        <div className="font-medium text-text-primary">{job?.name}</div>
                        <div className="text-sm text-text-secondary">
                          {job?.processedCount} of {job?.totalCount} assets processed
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="text-sm font-medium text-text-primary">
                          {Math.round((job?.processedCount / job?.totalCount) * 100)}%
                        </div>
                        <div className="text-xs text-text-secondary">
                          {formatJobTime(job?.startedAt)}
                        </div>
                      </div>
                      
                      {job?.status === 'processing' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCancelJob(job?.id)}
                          iconName="X"
                          className="text-destructive hover:text-destructive"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Job History Toggle */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-text-primary">Recent Jobs</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowJobHistory(!showJobHistory)}
            iconName={showJobHistory ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {showJobHistory ? 'Hide' : 'Show'} History
          </Button>
        </div>

        {/* Job History */}
        {showJobHistory && (
          <div className="space-y-2">
            {completedJobs?.slice(0, 5)?.map((job) => {
              const statusIcon = getJobStatusIcon(job?.status);
              return (
                <div key={job?.id} className="flex items-center justify-between p-3 hover:bg-muted rounded-lg transition-smooth">
                  <div className="flex items-center space-x-3">
                    <Icon name={statusIcon?.icon} size={16} className={statusIcon?.color} />
                    <div>
                      <div className="text-sm font-medium text-text-primary">{job?.name}</div>
                      <div className="text-xs text-text-secondary">
                        {job?.totalCount} assets • {formatJobTime(job?.completedAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getJobStatusBadge(job?.status)}`}>
                      {job?.status?.charAt(0)?.toUpperCase() + job?.status?.slice(1)}
                    </span>
                    
                    {job?.status === 'completed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Download"
                        className="text-text-secondary hover:text-text-primary"
                      />
                    )}
                  </div>
                </div>
              );
            })}
            
            {completedJobs?.length === 0 && (
              <div className="text-center py-8 text-text-secondary">
                <Icon name="Clock" size={32} className="mx-auto mb-2 opacity-50" />
                <p>No completed jobs yet</p>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button
              variant="outline"
              size="sm"
              iconName="FileSpreadsheet"
              iconPosition="left"
              fullWidth
            >
              Export Template
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Upload"
              iconPosition="left"
              fullWidth
            >
              Import Assets
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Settings"
              iconPosition="left"
              fullWidth
            >
              Job Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchProcessingPanel;